import { MetadataInfoDTO } from "../DTO/metadataInfoDTO";
import { TransformationDefinitionDTO } from "../DTO/transformationDefinitionDTO";
import { TransformationNodeDTO } from "../DTO/transformationNodeDTO";
import { TransformationDefinitionAI } from "../responses/generateTransformationResponse";
import { FieldDefinition } from "./fieldDefinition";
import { MetadataInfo } from "./metadataInfo";
import { TransformationNode } from "./transformationNode";
import { TransformationProtocol } from "./transformationProtocol";
import { TransformationRuleDefinition } from "./transformationRuleDefinition";

export class TransformationDefinition{
    public id? : number;
    public name! : string;
    public description! : string;
    public inputDefinitionType! : number; //0=File Definition, 1=Transformation Definition
    public inputDefinitionId! : number;
    public conformsWithFileDefinitionId? : number;
    public outputFileType! : string; //csv, fl, mrt-csv, mrt-fl, grt-csv, grt-fl, xml, json
    public fieldDelimiter? : string; // csv: single character delimiter
    public quotedFields : boolean = false; // csv: are fields enclosed in " => should default to YES to avoid confilcts with numeric separators
    public lineEnding? :  string //optional: if null use LF. all options: LF, CR, CRLF
    public createHeaderRow : boolean = false; //csv
    public trimValues : boolean = false; //csv
    public transformationProtocols : TransformationProtocol[] = []; //protocol for each field on how to transform
    public version? : number; //incremental version number set by server
    public published : boolean = false; //if true, then can be used for moovs and can be loaded by other users
    public added? : Date;
    public deleted : boolean = false;
    public fieldDefinitions : FieldDefinition[] = []; //created server side at saving/updating
    public ownedByEntityId? : number;
    public ownerEntityType?: string;
    public transformationTreeStructure? : string;
    public encoding? : string;
    public casing? : string;
    public defaultMetadataProtocol? : MetadataInfo[];
    public wizardSessionId? : number;

    public transformationRootNode? : TransformationNode; //if outputfiletype xml or json
    public inputDefinition!: any;

    public acceptFileTypes: string = '';

    constructor(){
        this.computeAcceptFileTypes();
    }

    public setInputDefinition(inputDefinition:any){
        //use this method to correctly instantiate the protocols
        this.inputDefinition=inputDefinition;
        if(this.inputDefinition) {
            this.transformationProtocols.forEach(p=>p.relateInputFieldDefinitions(inputDefinition.fieldDefinitions));
            this.computeAcceptFileTypes();
        }
    }

    public static mapFromDTO(param:TransformationDefinitionDTO):TransformationDefinition{ 
        if (!param) throw new Error("parameter is null")
        let obj = new TransformationDefinition();
        try
        {
            obj.id = param.id;
            obj.name = param.name;
            obj.description = param.description;
            obj.inputDefinitionType = param.inputDefinitionType?param.inputDefinitionType:0;
            obj.inputDefinitionId = param.inputDefinitionId;
            obj.conformsWithFileDefinitionId = param.conformsWithFileDefinitionId;
            obj.outputFileType = param.outputFileType;
            obj.fieldDelimiter = param.fieldDelimiter;
            obj.quotedFields = param.quotedFields;
            obj.lineEnding = param.lineEnding;
            obj.createHeaderRow = param.createHeaderRow;
            obj.trimValues = param.trimValues;
            obj.transformationProtocols = param.transformationProtocols.map(tp=>TransformationProtocol.mapFromDTO(tp));
            obj.version = param.version;
            obj.published = param.published;
            if(param.added) obj.added = new Date(param.added);
            obj.deleted = param.deleted;
            obj.ownedByEntityId = param.ownedByEntityId;
            obj.ownerEntityType = param.ownerEntityType;
            obj.fieldDefinitions = [];
            if(param.fieldDefinition) {
                let fdArray:any[] = JSON.parse(param.fieldDefinition);
                fdArray.forEach(fd=>{
                    let fieldDefinition = new FieldDefinition();
                    obj.fieldDefinitions.push(Object.assign(fieldDefinition,fd));
                });
            }
            if(param.transformationTreeStructure){
                let rootNode:TransformationNodeDTO = JSON.parse(param.transformationTreeStructure);
                obj.transformationRootNode=TransformationNode.mapFromDTO(rootNode);
                obj.transformationRootNode.isRoot=true;
                TransformationDefinition.createTransformationTree(obj,obj.transformationRootNode);
            }
            obj.encoding = param.encoding;
            obj.casing = param.casing;
            if(param.defaultMetadataProtocol) obj.defaultMetadataProtocol = TransformationDefinition.deserializeMetadataInfo(param.defaultMetadataProtocol);
            obj.wizardSessionId = param.wizardSessionId;            
        }
        catch(e)
        {
            console.log(e);
            
        }
        return obj;
    }

    public static mapFromAI(param:TransformationDefinitionAI):TransformationDefinition{ 
        if (!param) throw new Error("parameter is null")
        let obj = new TransformationDefinition();
        try
        {
            obj.name = param.name;
            obj.description = param.description;
            obj.inputDefinitionType = param.inputDefinitionType?param.inputDefinitionType:0;
            obj.inputDefinitionId = param.inputDefinitionId;
            obj.conformsWithFileDefinitionId = param.conformsWithFileDefinitionId;
            obj.outputFileType = param.outputFileType;
            obj.fieldDelimiter = param.fieldDelimiter;
            obj.quotedFields = param.quotedFields;
            obj.lineEnding = param.lineEnding;
            obj.createHeaderRow = param.createHeaderRow;
            obj.trimValues = param.trimValues;
            obj.transformationProtocols = param.transformationProtocols.map(tp=>TransformationProtocol.mapFromAI(tp));
            obj.fieldDefinitions = [];
            if(param.fieldDefinition) {
                let fdArray:any[] = JSON.parse(param.fieldDefinition);
                fdArray.forEach(fd=>{
                    let fieldDefinition = new FieldDefinition();
                    obj.fieldDefinitions.push(Object.assign(fieldDefinition,fd));
                });
            }
            if(param.transformationTreeStructure){
                let rootNode:TransformationNodeDTO = JSON.parse(param.transformationTreeStructure);
                obj.transformationRootNode=TransformationNode.mapFromDTO(rootNode);
                obj.transformationRootNode.isRoot=true;
                TransformationDefinition.createTransformationTree(obj,obj.transformationRootNode);
            }
            obj.encoding = param.encoding;
            obj.casing = param.casing;
            if(param.defaultMetadataProtocol) obj.defaultMetadataProtocol = TransformationDefinition.deserializeMetadataInfo(param.defaultMetadataProtocol);
        }
        catch(e)
        {
            console.log(e);
            
        }
        return obj;
    }

    private static createTransformationTree(td:TransformationDefinition, node:TransformationNode){
        node.fileType=td.outputFileType;
        node.protocol = td.transformationProtocols.find(tp=>tp.outputFieldIndex==node.protocolIndex)!;
        node.childNodes.forEach(cn=>{
            TransformationDefinition.createTransformationTree(td,cn);
        });
    }

    public static mapToDTO(param:TransformationDefinition):TransformationDefinitionDTO{ 
        if (!param) throw new Error("parameter is null")
        let dto = new TransformationDefinitionDTO();
        try
        {
            dto.id = param.id;
            dto.name = param.name;
            dto.description = param.description;
            dto.inputDefinitionType = param.inputDefinitionType;
            dto.inputDefinitionId = param.inputDefinition.id;
            dto.conformsWithFileDefinitionId = param.conformsWithFileDefinitionId;
            dto.outputFileType = param.outputFileType;
            dto.fieldDelimiter = param.fieldDelimiter;
            dto.lineEnding = param.lineEnding;
            dto.quotedFields = param.quotedFields;
            dto.createHeaderRow = param.createHeaderRow;
            dto.trimValues = param.trimValues;
            dto.transformationProtocols = param.transformationProtocols.map(tp=>TransformationProtocol.mapToDTO(tp));
            dto.version = param.version;
            dto.published = param.published;
            dto.ownedByEntityId = param.ownedByEntityId;
            dto.ownerEntityType = param.ownerEntityType;
            if(param.added) dto.added = param.added.toISOString();
            dto.deleted = param.deleted;
            if(param.fieldDefinitions && param.fieldDefinitions.length) dto.fieldDefinition = JSON.stringify(param.fieldDefinitions);
            if(param.transformationRootNode){
                let rootNodeDTO:TransformationNodeDTO = TransformationNode.mapToDTO(param.transformationRootNode);
                dto.transformationTreeStructure=JSON.stringify(rootNodeDTO);
            }
            dto.encoding = param.encoding;
            dto.casing = param.casing;
            if(param.defaultMetadataProtocol) dto.defaultMetadataProtocol = TransformationDefinition.serializeMetadataInfo(param.defaultMetadataProtocol);
            dto.wizardSessionId = param.wizardSessionId;
        }
        catch(e)
        {
            console.log(e);
        }
        return dto;
    }

    private static deserializeMetadataInfo(value:string){
        let metadataInfoArray:MetadataInfo[] = [];
        if(value){
            let mdiArray=JSON.parse(value);
            mdiArray.forEach((mdi:MetadataInfoDTO)=>{
                metadataInfoArray.push(MetadataInfo.mapFromDTO(mdi));
            });
        }
        return metadataInfoArray;
    }

    private static serializeMetadataInfo(mdiArray:MetadataInfo[]){
        let metadataInfoDTOArray:MetadataInfoDTO[] = [];
        if(mdiArray?.length>0){
            metadataInfoDTOArray=[];
            mdiArray.forEach(mdi=>{
                metadataInfoDTOArray.push(MetadataInfo.mapToDTO(mdi));
            });
        }
        return JSON.stringify(metadataInfoDTOArray);
    }

    get status():string{
        return this.deleted?"Inactive":"Active";
    }

    get outputFieldNames():string[]{
        //in lower case for duplicate comparison
        return this.transformationProtocols.map(tp=>tp.outputFieldName.toLowerCase());
    }

    get overrideableProtocols():TransformationProtocol[]{
        return this.transformationProtocols?.filter(tp=>!tp.startNewRecord);
    }

    //use DTO to create a deep copy
    get clone():TransformationDefinition{
        //memorize used transformationRuleDefinitions first
        let usedTransformationRuleDefinitions:TransformationRuleDefinition[]=[];
        this.transformationProtocols.forEach(tp=>{
            tp.transformationRules.forEach(tr=>{
                if(usedTransformationRuleDefinitions.findIndex(trd=>trd.id==tr.transformationRuleDefinition.id)==-1){
                    usedTransformationRuleDefinitions.push(tr.transformationRuleDefinition);
                }
            });
        });
        let mappedTransformationDefinition=TransformationDefinition.mapToDTO(this);
        mappedTransformationDefinition.id=0;
        mappedTransformationDefinition.name="";
        mappedTransformationDefinition.version=0;
        mappedTransformationDefinition.description="";
        mappedTransformationDefinition.published=false;
        mappedTransformationDefinition.transformationProtocols.forEach(p=>{
            p.id=0;
            p.transformationRules.forEach(r=>r.id=0);
        });
        let mapFromDTOTransFormationDefinition=TransformationDefinition.mapFromDTO(mappedTransformationDefinition);
        mapFromDTOTransFormationDefinition.setInputDefinition(this.inputDefinition);
        //relate transformationRuleDefinitions with clone
        mapFromDTOTransFormationDefinition.transformationProtocols.forEach(tp=>{
            tp.transformationRules.forEach(tr=>{
                tr.transformationRuleDefinition=usedTransformationRuleDefinitions.find(trd=>trd.id==tr.transformationRuleDefinitionId)!;
            });
        });
        return mapFromDTOTransFormationDefinition;
    }

    public resetIds(){
        this.id=0;
        this.transformationProtocols.forEach(p=>{
            p.id=0;
            p.transformationRules.forEach(r=>r.id=0);
        });
    }

    private computeAcceptFileTypes(){
        let types=".txt";
        if(this.inputDefinitionType==0){
            if(this.inputDefinition?.type=='csv') types+=",.csv,.xml";
        }
        else if(this.inputDefinitionType==1){
            if(this.inputDefinition?.outputFileType=='csv') types+=",.csv,.xml";
        }
        return types
    }
}
